// @flow
import type { CountryLanguage, CountryDescription, Template } from '~/core/types/beekeeper';
import type { EventGroup, Event } from '~/core/types/beekeeper/actions/events';
import type { ActionForm, ActionFormAttribute, ActionsState } from './types';

import { find, flowRight, identity, some, values } from 'lodash/fp';
import uuidv1 from 'uuid/v1';
import { actionTypes, getAttributeDefinition } from '~/core/constants/actions';
import { createSelector } from 'reselect';
import { toSeq, sortBy, map, toJS } from '~/core/utils/immutable';
import { getIconLocaleSuffix } from '~/core/state/config/selectors';
import { getUserLocale } from '~/core/state/user/selectors';
import { getIdFromProps } from '~/core/state/selectors';
import { getProductsByTypeList } from '~/core/state/products/selectors';
import { getIsHubNano1 } from '~/core/state/devices/selectors';
import { actionToForm, normaliseTemplate } from './utils';
import { IDLE } from './constants';

type State = { actions: ActionsState };
type Groups = {
    when: Array<ActionFormAttribute>,
    while: Array<ActionFormAttribute>,
    then: Array<ActionFormAttribute>
};

const getActionsState = (state: State) => state.actions || {};
const getTemplateIdFromProps = (_: Object, { templateId }: { templateId: string }) => templateId;
const getActionIdFromProps = (_: Object, props = {}) => props.actionId;
const getGroupFromProps = (_: Object, { group }: { group: EventGroup }) => group;

export const getTemplatesList = createSelector(getActionsState, actionsState => actionsState.templates);

export const getNormalisedTemplates = createSelector(getTemplatesList, getUserLocale, (list, locale) =>
    list.map(template => normaliseTemplate(template, locale))
);

export const getTemplate = createSelector(getTemplatesList, getTemplateIdFromProps, (list, templateId) =>
    list.find(t => t.id === templateId)
);

export const getIsLoadingActionsList = createSelector(getActionsState, actionState => actionState.loadingActions);

export const getActionsList = createSelector(getActionsState, actionsState => actionsState.actions);

export const getOrderedActionsList = createSelector(getActionsList, actions =>
    actions.sort((a, b) => {
        if (a.enabled && !b.enabled) return -1;
        if (!a.enabled && b.enabled) return 1;
        if (a.created < b.created) return 1;
        if (a.created > b.created) return -1;
        return 0;
    })
);

export const getActionsListOfProductId = createSelector(
    [state => getOrderedActionsList(state), getIdFromProps],
    (actions, productId) => actions.filter(action => action.events.some(event => event.id === productId))
);

export const getAction = createSelector(state => getActionsList(state), getActionIdFromProps, (list, actionId) =>
    list.find(a => a.id === actionId)
);

export const getActionForm = createSelector(getActionsState, actionsState => actionsState.actionForm);

export const currentSavingState = createSelector(getActionsState, actionsState => actionsState.savingState);

export const triggerActionsList = createSelector(getActionsState, actionsState => actionsState.triggerActionsList);

export const initialiseFormFromAction = createSelector(
    // $FlowFixMe
    getActionsState,
    getAction,
    getActionIdFromProps,
    (actionsState, action, actionId): ActionForm<ActionFormAttribute> | void =>
        action
            ? actionToForm(action)
            : console.error(`Unexpected Error: Action with id (${actionId}) should exist in the list of actions`)
);

export const listDevicesForActionAttribute = createSelector(
    getProductsByTypeList,
    flowRight(
        toJS,
        map(d => ({
            value: d.get('id'),
            label: d.getIn(['state', 'name'])
        })),
        sortBy(d => d.get('isGroup')),
        toSeq
    )
);

const createActionEvent = (templateEvent: Event | ActionFormAttribute) => ({
    ...templateEvent,
    guid: uuidv1(),
    // Property cannot be accessed on any member of intersection type
    // $FlowFixMe
    ...(templateEvent.settings
        ? {
              settings: Object.keys(templateEvent.settings).reduce((sets, key) => {
                  // Flow complains about possible `event.settings` undefined
                  // but it is guaranteed to exist by condition above
                  // $FlowFixMe
                  return { ...sets, [key]: templateEvent.settings[key].value };
              }, {})
          }
        : {})
});

const localeMetaFromTemplate = (template: Template, locale: CountryLanguage): CountryDescription =>
    template.meta[locale] || flowRight(find(l => l.default), values)(template.meta) || { name: '', description: '' };

export const initialiseFormFromTemplate = createSelector(
    identity,
    getActionsState,
    getTemplate,
    getTemplateIdFromProps,
    getUserLocale,
    getIconLocaleSuffix,
    (state, actionsState, template, templateId, locale, region): ActionForm<ActionFormAttribute> | void => {
        if (!template) {
            return console.error(
                `Unexpected Error: Template with id (${templateId}) should exist in the list of templates`
            );
        }

        const defineActionEventsFromTemplateEvent = templateEvent => {
            const definition = getAttributeDefinition(templateEvent.type, region);
            if (!definition.devices) {
                return [createActionEvent(templateEvent)];
            }
            // create the filter to either get all devices or just the first one depending `templateEvent.all`
            const filter = templateEvent.all ? identity : (arr: Array<*>) => (arr.length ? [arr[0]] : []);
            const devices = flowRight(filter, listDevicesForActionAttribute)(state, { types: definition.devices });
            return devices.map(device => ({ ...createActionEvent(templateEvent), id: device.value }));
        };

        const defineActionEvents = (acc, templateEvent) => {
            return templateEvent.required || templateEvent.default
                ? {
                      ...acc,
                      [templateEvent.group]: [
                          ...acc[templateEvent.group],
                          ...defineActionEventsFromTemplateEvent(templateEvent)
                      ]
                  }
                : acc;
        };

        const groups: Groups = template.events.reduce(defineActionEvents, { when: [], while: [], then: [] });

        return {
            enabled: true,
            name: localeMetaFromTemplate(template, locale).name,
            status: 'new',
            tags: template.meta.tags,
            template: {
                id: template.id,
                version: template.version
            },
            ...groups
        };
    }
);

const getEventFromProps = (_: Object, props: { event: ActionFormAttribute }) => props.event;
export const newActionEvent = createSelector(
    identity,
    getEventFromProps,
    getActionForm,
    getIconLocaleSuffix,
    (state, templateEvent, actionForm, region) => {
        const definition = getAttributeDefinition(templateEvent.type, region);
        const devices = listDevicesForActionAttribute(state, { types: definition.devices || [] }).filter(
            // check the device is not already used in another event of the same type
            device =>
                !(
                    actionForm !== 'none' &&
                    some({ type: templateEvent.type, id: device.value }, actionForm[templateEvent.group])
                )
        );

        return { ...createActionEvent(templateEvent), ...(devices.length ? { id: devices[0].value } : {}) };
    }
);

export const getActionsBeingDeleted = createSelector(getActionsState, actionsState => actionsState.actionsBeingDeleted);

export const eventsByGroup = createSelector(
    getTemplate,
    getGroupFromProps,
    getIsHubNano1,
    (template, group, isNano1): Array<Event> =>
        template
            ? template.events.reduce(
                  (acc, event) =>
                      event.group === group && (!isNano1 || !(event.incompatible || []).includes('NANO1'))
                          ? [...acc, event]
                          : acc,
                  []
              )
            : []
);

export const quickActionStatus = createSelector(
    triggerActionsList,
    getActionIdFromProps,
    (triggerActionsList, actionId) => {
        const actionStatus = triggerActionsList.find(a => a.id === actionId);
        if (actionStatus) {
            return actionStatus;
        }
        return { displayState: IDLE };
    }
);

export const isQuickAction = createSelector(getAction, (action = {}) =>
    some(['type', actionTypes.QUICK_ACTION], action.events)
);

// $FlowFixMe
export const isProductUsedInAction = createSelector(
    state => getActionsList(state),
    getIdFromProps,
    (actionsList, productId) => some(a => some(['id', productId], a.events), actionsList)
);



// WEBPACK FOOTER //
// ./src/core/state/actions/selectors.js